import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateQuestions } from "../../services/aiInterviewService";

const TYPES = [
  { id: "hr", label: "HR Round", desc: "Culture fit, motivation, soft skills" },
  { id: "technical", label: "Technical Round", desc: "Domain knowledge, technical depth" },
];

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];
const DURATIONS = [15, 30, 45];

export default function InterviewSetup() {
  const navigate = useNavigate();
  const [type, setType] = useState("hr");
  const [role, setRole] = useState("");
  const [difficulty, setDifficulty] = useState("Intermediate");
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleStart() {
    if (!role.trim()) {
      setError("Enter the role you're preparing for.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const questions = await generateQuestions({ type, role, difficulty, duration, persona: "professional" });
      navigate("/erp/ai-interview/session", {
        state: { type, role, difficulty, duration, questions },
      });
    } catch (e) {
      setError(e.message || "Could not start the interview. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.stage}>
      <div style={styles.glow} />
      <div style={styles.card}>
        <p style={styles.eyebrow}>BEFORE THE SCENE</p>
        <h1 style={styles.title}>What are we rehearsing today?</h1>
        <p style={styles.subtitle}>Pick a round, name the role, and step on stage.</p>

        <div style={styles.typeRow}>
          {TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              style={{
                ...styles.typeCard,
                ...(type === t.id ? styles.typeCardActive : {}),
              }}
            >
              <span style={styles.typeLabel}>{t.label}</span>
              <span style={styles.typeDesc}>{t.desc}</span>
            </button>
          ))}
        </div>

        <label style={styles.label}>Role you're preparing for</label>
        <input
          style={styles.input}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. Frontend Developer, HR Executive"
        />

        <div style={styles.rowSplit}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Difficulty</label>
            <div style={styles.pillRow}>
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  style={{ ...styles.pill, ...(difficulty === d ? styles.pillActive : {}) }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Duration</label>
            <div style={styles.pillRow}>
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDuration(d)}
                  style={{ ...styles.pill, ...(duration === d ? styles.pillActive : {}) }}
                >
                  {d}m
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <button style={styles.cta} onClick={handleStart} disabled={loading}>
          {loading ? "Setting the stage…" : "Step on stage ▸"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  stage: {
    minHeight: "100vh",
    background: "#10151F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  glow: {
    position: "absolute",
    top: "-20%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "700px",
    height: "700px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(201,154,75,0.18) 0%, rgba(201,154,75,0) 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    width: "560px",
    maxWidth: "92vw",
    background: "#161C29",
    border: "1px solid #232B3B",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
  },
  eyebrow: {
    color: "#C99A4B",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: "11px",
    letterSpacing: "2px",
    marginBottom: "10px",
  },
  title: {
    fontFamily: "'Fraunces', serif",
    color: "#EFEAE1",
    fontSize: "30px",
    fontWeight: 600,
    margin: "0 0 8px 0",
    lineHeight: 1.25,
  },
  subtitle: {
    color: "#8A93A6",
    fontSize: "13px",
    marginBottom: "28px",
  },
  typeRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "24px",
  },
  typeCard: {
    flex: 1,
    textAlign: "left",
    background: "#1B2231",
    border: "1px solid #2A3345",
    borderRadius: "14px",
    padding: "16px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  typeCardActive: {
    border: "1px solid #C99A4B",
    background: "#20263A",
  },
  typeLabel: {
    color: "#EFEAE1",
    fontWeight: 600,
    fontSize: "14px",
  },
  typeDesc: {
    color: "#6B7488",
    fontSize: "11px",
  },
  label: {
    color: "#8A93A6",
    fontSize: "11px",
    letterSpacing: "0.5px",
    display: "block",
    marginBottom: "8px",
  },
  input: {
    width: "100%",
    background: "#1B2231",
    border: "1px solid #2A3345",
    borderRadius: "10px",
    padding: "12px 14px",
    color: "#EFEAE1",
    fontSize: "13px",
    marginBottom: "22px",
    boxSizing: "border-box",
  },
  rowSplit: {
    display: "flex",
    gap: "20px",
    marginBottom: "26px",
  },
  pillRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    background: "#1B2231",
    border: "1px solid #2A3345",
    borderRadius: "999px",
    padding: "7px 14px",
    color: "#8A93A6",
    fontSize: "12px",
    cursor: "pointer",
  },
  pillActive: {
    border: "1px solid #C99A4B",
    color: "#C99A4B",
    background: "#20263A",
  },
  error: {
    color: "#D9705A",
    fontSize: "12px",
    marginBottom: "14px",
  },
  cta: {
    width: "100%",
    background: "#C99A4B",
    color: "#10151F",
    border: "none",
    borderRadius: "12px",
    padding: "14px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
};
